import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import { useTranslation } from 'react-i18next';

import { Tenant } from '../../../types/tenants';
import { useTenantsRequests } from '../../../api/tenants';
import styles from './editTenant.module.css';
import { useSnackbar } from 'notistack';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../consts';

interface EditTenantProps {
  tenant?: Tenant;
  onSuccess?: () => void;
}

type FormData = {
  name: string;
  pesel: string;
  nip: string;
  regon: string;
  krs: string;
  street: string;
  city: string;
  zip_code: string;
  country: string;
  logo?: File | null;
  website: string;
};

const defaultFormData: FormData = {
  name: '',
  pesel: '',
  nip: '',
  regon: '',
  krs: '',
  street: '',
  city: '',
  zip_code: '',
  country: 'PL',
  logo: undefined,
  website: '',
};

export const EditTenant: React.FC<EditTenantProps> = ({ tenant, onSuccess }) => {
  const { t } = useTranslation();
  const { updateFormData, error, loading } = useTenantsRequests();
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        pesel: tenant.pesel || '',
        nip: tenant.nip || '',
        regon: tenant.regon || '',
        krs: tenant.krs || '',
        street: tenant.street || '',
        city: tenant.city || '',
        zip_code: tenant.zip_code || '',
        country: tenant.country || 'PL',
        logo: undefined,
        website: tenant.website || '',
      });
    }
  }, [tenant]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setFormData((prevState) => ({
      ...prevState,
      logo: file,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tenant?.id) {
      return;
    }

    const { success } = await updateFormData({
      id: tenant.id,
      ...formData,
      country: formData.country.toUpperCase(),
    });

    if (success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      onSuccess?.();
      enqueueSnackbar(t("Workspace updated successfully"), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Workspace update failed"), ERROR_SNACKBAR);
    }
  };

  if (!tenant) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="name"
          label={t('Workspace name')}
          size="small"
          fullWidth
          value={formData.name}
          onChange={handleInputChange}
          required
        />
        {error?.name?.[0]?.message && <div className={styles.errorMessage}>{error?.name?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="pesel"
          label={t('PESEL')}
          size="small"
          fullWidth
          value={formData.pesel}
          onChange={handleInputChange}
        />
        {error?.pesel?.[0]?.message && <div className={styles.errorMessage}>{error?.pesel?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="nip"
          label={t('NIP')}
          size="small"
          fullWidth
          value={formData.nip}
          onChange={handleInputChange}
        />
        {error?.nip?.[0]?.message && <div className={styles.errorMessage}>{error?.nip?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="regon"
          label={t('REGON')}
          size="small"
          fullWidth
          value={formData.regon}
          onChange={handleInputChange}
        />
        {error?.regon?.[0]?.message && <div className={styles.errorMessage}>{error?.regon?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="krs"
          label={t('KRS')}
          size="small"
          fullWidth
          value={formData.krs}
          onChange={handleInputChange}
        />
        {error?.krs?.[0]?.message && <div className={styles.errorMessage}>{error?.krs?.[0]?.message}</div>}
      </Box>

      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="website"
          label={t('Website')}
          size="small"
          fullWidth
          value={formData.website}
          onChange={handleInputChange}
        />
        {error?.website?.[0]?.message && <div className={styles.errorMessage}>{error?.website?.[0]?.message}</div>}
      </Box>

      <Box sx={{ width: "100%" }}>
        <Typography variant="body2" fontWeight={500}>
          {t('Company address')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('The registered address of the company. Copigo issues the subscription invoices to it.')}
        </Typography>
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="street"
          label={t('Street')}
          size="small"
          fullWidth
          value={formData.street}
          onChange={handleInputChange}
        />
        {error?.street?.[0]?.message && <div className={styles.errorMessage}>{error?.street?.[0]?.message}</div>}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="zip_code"
          label={t('Zip code')}
          size="small"
          fullWidth
          value={formData.zip_code}
          onChange={handleInputChange}
        />
        {error?.zip_code?.[0]?.message && <div className={styles.errorMessage}>{error?.zip_code?.[0]?.message}</div>}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="city"
          label={t('City')}
          size="small"
          fullWidth
          value={formData.city}
          onChange={handleInputChange}
        />
        {error?.city?.[0]?.message && <div className={styles.errorMessage}>{error?.city?.[0]?.message}</div>}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="country"
          label={t('Country')}
          size="small"
          fullWidth
          value={formData.country}
          onChange={handleInputChange}
          slotProps={{ htmlInput: { maxLength: 2, style: { textTransform: 'uppercase' } } }}
        />
        {error?.country?.[0]?.message && <div className={styles.errorMessage}>{error?.country?.[0]?.message}</div>}
      </Box>

      <Box sx={{ width: "100%" }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
          {t('Company logo')}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileOutlined />}
          >
            {t('Add file')}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              hidden
            />
          </Button>
          {formData.logo && (
            <Typography variant="body2" color="text.secondary">
              {formData.logo.name}
            </Typography>
          )}
        </Box>
        {error?.logo?.[0]?.message && <div className={styles.errorMessage}>{error?.logo?.[0]?.message}</div>}
      </Box>

      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          type="submit"
          disabled={loading}
        >
          {loading ? t('Saving...') : t('Update workspace')}
        </Button>
      </Box>
    </Box>
  );
};
